import { useSyncExternalStore } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { connectionStore, trackRequest, outcomeForStatus } from './state';
import './connection.css';
import { cn } from '@/lib/utils';

/**
 * Real-time connection feedback, worked out in `state.ts`:
 *   loading  — a bee buzzing while mail loads (only after SHOW_DELAY_MS, so quick loads don't flash)
 *   slow     — data is coming, slowly
 *   offline  — nothing is coming back; "Try again" re-asks the server and refetches what's on screen
 * A floating pill centred in the viewport rather than a bar, so it never pushes the message list around.
 * z-[60]: above dialogs (z-50), which load data too and rely on this pill instead of spinners.
 */
/** The one loading animation in B-Mail. Also used where something loads without a request to time (compose pop-out). */
export function BuzzingBee({ slow = false }: { slow?: boolean }) {
  return <span aria-hidden="true" className={cn(slow ? 'bmail-buzz-slow' : 'bmail-buzz', 'inline-block text-3xl leading-none')}>🐝</span>;
}

export function ConnectionBanner() {
  const status = useSyncExternalStore(connectionStore.subscribe, connectionStore.getSnapshot, () => 'idle' as const);
  const queryClient = useQueryClient();

  if (status === 'idle') return null;

  const tryAgain = () => {
    connectionStore.retry();
    // The health check guarantees one request even when no query is on screen, so the retry settles.
    // Fetched here rather than through api/client.ts, which imports this module (no import cycle).
    const settle = trackRequest();
    fetch(`${import.meta.env.VITE_API_BASE_URL || '/api'}/health`, { credentials: 'include' })
      .then((r) => settle(outcomeForStatus(r.status)), () => settle('network-error'));
    queryClient.refetchQueries({ type: 'active' });
  };

  return (
    <div
      role="status"
      aria-live="polite"
      className="pointer-events-none fixed inset-0 z-[60] flex items-center justify-center px-4"
    >
      <div
        className={cn(
          'pointer-events-auto flex max-w-full items-center gap-3 rounded-full border bg-bmail-surface px-6 py-3 text-base shadow-xl',
          status === 'offline' ? 'border-bmail-error/50' : 'border-bmail-border',
        )}
      >
        {status === 'loading' && (
          <>
            <BuzzingBee />
            <span className="text-bmail-text-secondary">Fetching your mail…</span>
          </>
        )}
        {status === 'slow' && (
          <>
            <BuzzingBee slow />
            <span className="font-medium text-bmail-text">Slow connection</span>
            <span className="hidden text-bmail-text-secondary sm:inline">— still on its way</span>
          </>
        )}
        {status === 'offline' && (
          <>
            <span aria-hidden="true" className="text-3xl leading-none">🐝🚫</span>
            <span className="font-medium text-bmail-error">No connection</span>
            <button
              onClick={tryAgain}
              className="ml-1 rounded-full bg-bmail-goldfill px-4 py-1.5 text-sm font-semibold text-bmail-on-accent hover:bg-bmail-goldfill-hover"
            >
              Try again
            </button>
          </>
        )}
      </div>
    </div>
  );
}
